import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    this.addEventListener(DialogOpenEvent.eventName, this.#startCheckoutVisibility);
    this.addEventListener(DialogCloseEvent.eventName, this.#stopCheckoutVisibility);

    if (this.refs.dialog?.open) this.#startCheckoutVisibility();

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
    this.removeEventListener(DialogOpenEvent.eventName, this.#startCheckoutVisibility);
    this.removeEventListener(DialogCloseEvent.eventName, this.#stopCheckoutVisibility);
    this.#stopCheckoutVisibility();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #handleCartAdd = (event) => {
    if (!(event instanceof CartAddEvent) || event.detail.data?.didError) return;
    if (event.detail.data?.source === 'quick-order-remove-all') return;

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }


  #checkoutAbort = null;
  #checkoutMutation = null;
  #checkoutResize = null;
  #checkoutFrame = 0;
  #checkoutBar = null;

  #startCheckoutVisibility = () => {
    this.#stopCheckoutVisibility();
    const dialog = this.refs.dialog;
    this.#checkoutAbort = new AbortController();
    const options = { signal: this.#checkoutAbort.signal, passive: true };
    dialog.addEventListener('scroll', this.#scheduleCheckoutVisibility, { ...options, capture: true });
    window.addEventListener('resize', this.#scheduleCheckoutVisibility, options);
    window.visualViewport?.addEventListener('resize', this.#scheduleCheckoutVisibility, options);
    window.visualViewport?.addEventListener('scroll', this.#scheduleCheckoutVisibility, options);
    const inner = dialog.querySelector('.cart-drawer__inner');
    this.#checkoutMutation = new MutationObserver((records) => {
      if (records.some(({ target }) => !this.#checkoutBar?.contains(target))) {
        this.#scheduleCheckoutVisibility();
      }
    });
    this.#checkoutMutation.observe(dialog, {
        subtree: true, childList: true, characterData: true,
        attributes: true, attributeFilter: ['disabled', 'class', 'open']
    });
    this.#checkoutResize = new ResizeObserver(this.#scheduleCheckoutVisibility);
    if (inner) this.#checkoutResize.observe(inner);
    this.#checkoutResize.observe(dialog);
    this.#syncCheckoutVisibility();
  };

  #stopCheckoutVisibility = () => {
    this.#checkoutAbort?.abort();
    this.#checkoutMutation?.disconnect();
    this.#checkoutResize?.disconnect();
    cancelAnimationFrame(this.#checkoutFrame);
    this.#checkoutFrame = 0;
    this.#checkoutBar?.remove();
    this.#checkoutBar = null;
  };

  #scheduleCheckoutVisibility = () => {
    if (this.#checkoutFrame) return;
    this.#checkoutFrame = requestAnimationFrame(() => {
      this.#checkoutFrame = 0;
      this.#syncCheckoutVisibility();
    });
  };

  #syncCheckoutVisibility = () => {
    const dialog = this.refs.dialog;
    if (!dialog?.open) return;
    if (this.#checkoutBar && this.#checkoutBar.parentElement !== dialog) {
      this.#checkoutBar.remove();
      this.#checkoutBar = null;
    }
    const original = dialog.querySelector('.cart-drawer__summary .cart__checkout-button');
    const content = dialog.querySelector('.cart-drawer__content');
    if (!original || !content) {
      if (this.#checkoutBar) this.#checkoutBar.hidden = true;
      return;
    }
    this.#updateStickyState();
    this.#checkoutResize?.observe(original);
    this.#checkoutResize?.observe(content);
    const summary = original.closest('.cart-drawer__summary');
    if (summary) this.#checkoutResize?.observe(summary);
    const rect = original.getBoundingClientRect();
    const drawerRect = dialog.getBoundingClientRect();
    const contentRect = content.getBoundingClientRect();
    const viewport = window.visualViewport;
    const viewportBottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
    const bottom = Math.min(drawerRect.bottom, contentRect.bottom, viewportBottom);
    const shouldFloat = rect.height > 0 && rect.bottom > bottom - 12;
    if (!this.#checkoutBar && shouldFloat) {
      const bar = document.createElement('div');
      bar.className = 'cart-drawer__floating-checkout cart__ctas';
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'cart__checkout-button button';
      button.addEventListener('click', () => {
        const checkout = dialog.querySelector('.cart-drawer__summary .cart__checkout-button');
        if (checkout && !checkout.disabled) checkout.click();
      });
      bar.append(button);
      dialog.append(bar);
      this.#checkoutBar = bar;
    }
    const bar = this.#checkoutBar;
    if (!bar) return;
    const button = bar.querySelector('button');
    const label = original.textContent.trim();
    if (button.textContent !== label) button.textContent = label;
    button.disabled = original.disabled;
    bar.style.bottom = Math.max(0, drawerRect.bottom - viewportBottom) + 'px';
    if (!shouldFloat && bar.contains(document.activeElement)) original.focus({ preventScroll: true });
    bar.hidden = !shouldFloat;
  };

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
